import { classes } from "polytype";
import assertObjectFound from "../../base/assertObjectFound.js";
import {
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base/index.js";
import { config } from "../../config/config.js";
import type { Project } from "../../project/index.js";
import { provideReact, type AsyncResourceVariant } from "../../react/index.js";
import type {
  VolumeData,
  VolumeListItemData,
  VolumeListQueryData,
} from "./types.js";

export class Volume extends ReferenceModel {
  public readonly stackId: string;

  public constructor(id: string, stackId: string) {
    super(id);
    this.stackId = stackId;
  }

  public static ofId(id: string, stackId: string) {
    return new Volume(id, stackId);
  }

  public static find = provideReact(async (id: string, stackId: string) => {
    const data = await config.behaviors.volume.find(id, stackId);
    if (data !== undefined) {
      return new VolumeDetailed(data);
    }
  });

  public static get = provideReact(async (id: string, stackId: string) => {
    const volume = await this.find(id, stackId);
    assertObjectFound(volume, "volume", id);
    return volume;
  });

  public findDetailed = provideReact(
    () => Volume.find(this.id, this.stackId),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<VolumeDetailed | undefined>>;

  public getDetailed = provideReact(
    () => Volume.get(this.id, this.stackId),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<VolumeDetailed>>;

  public static query(project: Project, query: VolumeListQueryData = {}) {
    return new VolumeListQuery(project, query);
  }

  public static async create(projectId: string, name: string) {
    const response = await config.behaviors.volume.create(projectId, name);

    if ("id" in response) {
      return new Volume(response.id, projectId);
    }

    return response;
  }

  public async delete() {
    await config.behaviors.volume.delete(this.id, this.stackId);
  }

  public static generateRandomName(initialName: string): string {
    let result = "";
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const charactersLength = characters.length;
    for (let i = 0; i < 4; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return `${initialName}-${result}`;
  }
}

class VolumeCommon extends classes(
  DataModel<VolumeListItemData | VolumeData>,
  Volume,
) {
  public readonly name: string;

  public constructor(data: VolumeListItemData | VolumeData) {
    super([data], [data.id, data.stackId]);
    this.name = data.name;
  }
}

export class VolumeDetailed extends classes(
  VolumeCommon,
  DataModel<VolumeData>,
) {
  public constructor(data: VolumeData) {
    super([data], [data]);
  }
}

export class VolumeListItem extends classes(
  VolumeCommon,
  DataModel<VolumeListItemData>,
) {
  public constructor(data: VolumeListItemData) {
    super([data], [data]);
  }
}

export class VolumeListQuery extends ListQueryModel<VolumeListQueryData> {
  public readonly project: Project;

  public constructor(project: Project, query: VolumeListQueryData = {}) {
    super(query, {
      dependencies: [project.id],
    });
    this.project = project;
  }

  public refine(query: VolumeListQueryData) {
    return new VolumeListQuery(this.project, {
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items } = await config.behaviors.volume.list(
      this.project.id,
      this.query,
    );
    return new VolumeList(
      this.project,
      this.query,
      items.map((v) => new VolumeListItem(v)),
    );
  }, [this.queryId, "volumes"]);
}

export class VolumeList extends classes(
  VolumeListQuery,
  ListDataModel<VolumeListItem>,
) {
  public constructor(
    project: Project,
    query: VolumeListQueryData,
    volumes: VolumeListItem[],
  ) {
    super([project, query], [volumes, volumes.length]);
  }
}
